import { useMemo, useState } from "react";
import { api, type ReadingResponse, type UserView } from "../api";
import { CardBack, CardFace, Fan, OneBack, ThreeBacks } from "../art";
import { LockedList, Notes, Overlay, SectionBlock, Spinner } from "../ui";

export type SpreadKind = "day" | "one" | "three";

const POSITIONS = ["Настоящее", "Причины", "Исход"];
const TITLES: Record<SpreadKind, string> = { day: "Карта дня", one: "Расклад на одну карту", three: "Расклад на три карты" };
const QUESTION_PRESETS = ["Что происходит в моих отношениях?", "Как будет с деньгами?", "Какой мой следующий шаг?", "Что мне сейчас важно увидеть?"];

/** Вкладка «Расклады»: карта дня, расклады на одну и три карты — как в референсе. */
export function Spreads({ user, onStart, onPaywall }: { user: UserView; onStart: (k: SpreadKind) => void; onPaywall: () => void }) {
  const start = (k: SpreadKind) => (k !== "day" && user.tier === "free" ? onPaywall() : onStart(k));
  return (
    <div className="warm">
      <div className="pagehead">
        <h1>Расклады</h1>
        <p>Задай свой самый сокровенный вопрос и получи ответ на него</p>
      </div>
      <div className="sheet" style={{ minHeight: "70vh" }}>
        <button className="card fancard" style={{ display: "block", width: "100%" }} onClick={() => start("day")}>
          <Fan />
          <h3>Вытяни карту дня</h3>
          <p>Это не предсказание, а повод остановиться и задать себе вопрос.</p>
        </button>
        <div className="spreadcard">
          <div>
            <h3>Расклад на одну карту</h3>
            <p>Одна карта для ответа на вопрос или понимания текущей ситуации</p>
            <button className="outline" onClick={() => start("one")}>
              Сделать расклад
            </button>
          </div>
          <OneBack />
        </div>
        <div className="spreadcard">
          <div>
            <h3>Расклад на три карты</h3>
            <p>Три карты: настоящее, причины и исход ситуации</p>
            <button className="outline" onClick={() => start("three")}>
              Сделать расклад
            </button>
          </div>
          <ThreeBacks />
        </div>
      </div>
    </div>
  );
}

const DECK_VIEW = 21;

interface FlowProps {
  kind: SpreadKind;
  onClose: () => void;
  onPaywall: () => void;
  onLoaded: (r: ReadingResponse) => void;
}

/** Выбор карт «рубашкой вверх» → результат. Карта дня — одна на сутки независимо от выбранной рубашки. */
export function SpreadFlow({ kind, onClose, onPaywall, onLoaded }: FlowProps) {
  const [picks, setPicks] = useState<number[]>([]);
  const [data, setData] = useState<ReadingResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState(kind === "day");
  const need = kind === "three" ? 3 : 1;
  const idx = useMemo(() => Array.from({ length: DECK_VIEW }, (_, i) => i), []);

  const pick = async (i: number) => {
    if (busy || picks.includes(i)) return;
    const next = [...picks, i];
    setPicks(next);
    if (next.length < need) return;
    setBusy(true);
    try {
      const q = question.trim();
      const r = await api.reading("cards", { kind, ...(kind === "day" ? {} : { picks: next.join(","), ...(q ? { question: q } : {}) }) });
      setData(r);
      onLoaded(r);
    } catch {
      setFailed(true);
      setPicks([]);
    } finally {
      setBusy(false);
    }
  };

  if (data) return <SpreadResult kind={kind} data={data} onClose={onClose} onPaywall={onPaywall} />;

  if (!asked) {
    return (
      <Overlay title="Какой вопрос не даёт покоя?" onClose={onClose}>
        <div className="sheet">
          <input
            className="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Напиши свой вопрос"
          />
          <div className="presetgrid">
            {QUESTION_PRESETS.map((p) => (
              <button key={p} className="outline" onClick={() => setQuestion(p)}>
                {p}
              </button>
            ))}
          </div>
          <button className="primary" style={{ display: "block", width: "100%", marginTop: 16 }} onClick={() => setAsked(true)}>
            Перемешать колоду
          </button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay title={kind === "three" ? "Выбери 3 карты" : "Выбери карту"} onClose={onClose}>
      {kind === "three" && (
        <div className="slots">
          {POSITIONS.map((p, i) => (
            <div className="slot" key={p}>
              <div className="ph">{picks[i] !== undefined && <CardBack />}</div>
              {p}
            </div>
          ))}
        </div>
      )}
      {busy && <Spinner />}
      {failed && <p className="center" style={{ color: "var(--on-dark)" }}>Не удалось открыть карты. Попробуйте ещё раз.</p>}
      {kind === "three" ? (
        <div className="deckrows">
          {[0, 1, 2].map((r) => (
            <div className="deckrow" key={r}>
              {idx.slice(r * 7, r * 7 + 7).map((i) => (
                <button key={i} className={picks.includes(i) ? "picked" : ""} onClick={() => void pick(i)} aria-label={`Карта ${i + 1}`}>
                  <CardBack />
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="cardgrid">
          {idx.slice(0, 12).map((i) => (
            <button key={i} onClick={() => void pick(i)} aria-label={`Карта ${i + 1}`}>
              <CardBack />
            </button>
          ))}
        </div>
      )}
    </Overlay>
  );
}

function parseCard(heading: string | undefined): { n: number; name: string } | null {
  const m = /(\d+):\s*(.+)$/u.exec(heading ?? "");
  return m ? { n: Number(m[1]), name: m[2]!.trim() } : null;
}

function SpreadResult({ kind, data, onClose, onPaywall }: { kind: SpreadKind; data: ReadingResponse; onClose: () => void; onPaywall: () => void }) {
  const doc = data.reading.doc;
  const cards = doc.sections.map((s) => ({ s, card: parseCard(s.heading) })).filter((x) => x.card !== null);

  return (
    <Overlay title={TITLES[kind]} onClose={onClose}>
      <div className="faceview">
        {cards.length === 0 ? (
          <div className="faces">
            {(doc.numbers.cards as number[]).map((_, i) => (
              <div key={i}>
                <CardBack />
              </div>
            ))}
          </div>
        ) : kind === "three" ? (
          <div className="faces">
            {cards.map(({ card }, i) => (
              <div key={i}>
                <CardFace n={card!.n} name={card!.name} />
              </div>
            ))}
          </div>
        ) : (
          <div className="bigface">
            <CardFace n={cards[0]!.card!.n} name={cards[0]!.card!.name} />
          </div>
        )}
      </div>
      <div className="sheetbody">
        {doc.sections.map((s) => (
          <SectionBlock key={s.id} section={s} />
        ))}
        <LockedList doc={doc} onPaywall={onPaywall} />
        <Notes doc={doc} />
      </div>
    </Overlay>
  );
}
